import type {
  AccountSetting,
  AccountSettingDataType,
  AccountSettingType
} from '../../types/account-settings';
import { emitter } from '@/shared/lib/emitter';
import { JsonHelper } from '../util/json-helper';
import { appConfig } from './app-config';
import { userPermissions } from './user-permissions';

export type { AccountSetting, AccountSettingDataType, AccountSettingType };

class AccountConfig {
  #settings: Record<AccountSettingType, AccountSetting>;

  constructor(settings: AccountSetting[]) {
    this.#settings = settings.reduce(
      (res, setting) => {
        const data = JsonHelper.safeJsonParse(<string>setting.data);
        res[setting.type] = {
          ...setting,
          data
        };
        return res;
      },
      {} as Record<AccountSettingType, AccountSetting>
    );
  }

  #get<Type extends AccountSettingType>(type: Type) {
    return this.#settings[type];
  }

  get<Type extends AccountSettingType>(type: Type) {
    const setting = this.#get(type);
    if (!setting) {
      return undefined;
    }
    return setting.data as AccountSettingDataType[Type];
  }

  getter<Type extends AccountSettingType>(type: Type) {
    return () => this.get(type);
  }

  set<Type extends AccountSettingType>(type: Type, value: AccountSettingDataType[Type]) {
    const setting = this.#get(type);
    if (!setting) {
      return;
    }
    setting.data = Object.assign({}, value);
  }

  has<Type extends AccountSettingType>(type: Type) {
    const setting = this.#get(type);
    return setting !== undefined;
  }
}

export const config = {
  settings: new AccountConfig(window.AccountSettings || []),
  appConfig,
  userPermissions
};

emitter.on('setting', (message) => {
  if (message.type === 'dictionaries-update:edit' && message.data.meta.type === 'account_division') {
    config.settings.set('divisions-update', message.data.settings);
  }
});
